import { Component, ReactNode, ErrorInfo } from 'react';
import * as Sentry from "@sentry/react";
import { reloadAfterChunkLoadError } from '../utils/chunkLoadRecovery';
import './ErrorBoundary.css';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  copied: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, copied: false };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    if (reloadAfterChunkLoadError(error)) return;
    console.error("Error caught in ErrorBoundary:", error, errorInfo);
    Sentry.captureException(error);
  }

  resetErrorBoundary = (): void => {
    this.setState({ hasError: false, error: null, copied: false });
  };

  copyErrorToClipboard = (): void => {
    if (this.state.error) {
      navigator.clipboard.writeText(this.state.error.stack || this.state.error.message)
        .then(() => this.setState({ copied: true }))
        .catch(() => {});
    }
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="sky-error-boundary">
          <div className="sky-error-card">
            <div className="sky-error-icon">
              <svg width={32} height={32} viewBox="0 0 24 24" fill="none" stroke="var(--red)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h3 className="sky-error-title">Something went wrong</h3>
            <p className="sky-error-message">{this.state.error?.message}</p>
            <div className="sky-error-actions">
              <button className="sky-btn sky-btn-primary" onClick={this.resetErrorBoundary}>
                Try Again
              </button>
              <button className="sky-btn sky-btn-ghost" onClick={() => window.location.reload()}>
                Hard Reload
              </button>
              <button className="sky-btn sky-btn-ghost" onClick={this.copyErrorToClipboard}>
                {this.state.copied ? 'Copied!' : 'Copy Error'}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
